import { X, Mail, MessageCircle } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { copyToClipboard } from '../../utils/clipboard';

interface ShareOptionsModalProps {
  isOpen: boolean;
  onClose: () => void;
  title?: string;
  message?: string;
  url?: string;
  fileName?: string;
  shareType?:
    | 'invoice'
    | 'report'
    | 'document'
    | 'profile'
    | 'record'
    | 'general';
}

export function ShareOptionsModal({
  isOpen,
  onClose,
  title = 'Share',
  message = 'Check this out!',
  url = window.location.href,
  fileName,
  shareType = 'general',
}: ShareOptionsModalProps) {
  const { t } = useTranslation();
  if (!isOpen) return null;

  const handleWhatsAppShare = () => {
    const text = encodeURIComponent(`${message}\n\n${url}`);
    const whatsappUrl = `https://wa.me/?text=${text}`;
    window.open(whatsappUrl, '_blank');
    toast.success(t('shareModal.toastOpeningWhatsapp'));
    onClose();
  };

  const handleEmailShare = () => {
    const subject = encodeURIComponent(title);
    const body = encodeURIComponent(`${message}\n\n${url}`);
    const mailtoUrl = `mailto:?subject=${subject}&body=${body}`;
    window.location.href = mailtoUrl;
    toast.success(t('shareModal.toastOpeningEmail'));
    onClose();
  };

  const handleTelegramShare = () => {
    const telegramUrl = `https://t.me/share/url?url=${encodeURIComponent(url)}&text=${encodeURIComponent(message)}`;
    window.open(telegramUrl, '_blank');
    toast.success(t('shareModal.toastOpeningTelegram'));
    onClose();
  };

  const handleCopyLink = () => {
    copyToClipboard(url);
    toast.success(t('shareModal.toastLinkCopied'));
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black/50 dark:bg-black/70 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-[#16151D] rounded-lg max-w-md w-full p-6 relative">
        {/* Header */}
        <div className="flex justify-between items-center mb-6">
          <h3
            className="text-xl text-[#2D2F7E] dark:text-white"
            style={{ fontFamily: 'Sora, sans-serif', fontWeight: 700 }}
          >
            {t('shareModal.shareVia')}
          </h3>
          <button
            onClick={onClose}
            className="text-gray-400 dark:text-white/60 hover:text-gray-600 dark:hover:text-white/80 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Share Options */}
        <div className="space-y-3">
          {/* WhatsApp */}
          <button
            onClick={handleWhatsAppShare}
            className="w-full flex items-center gap-4 p-4 border border-gray-200 dark:border-white/20 rounded-lg hover:bg-gray-50 dark:hover:bg-white/5 transition-colors group"
          >
            <div className="w-12 h-12 bg-green-100 dark:bg-green-900/20 rounded-full flex items-center justify-center group-hover:bg-green-200 dark:group-hover:bg-green-900/30 transition-colors">
              <svg
                className="w-6 h-6 text-green-600 dark:text-green-400"
                viewBox="0 0 24 24"
                fill="currentColor"
              >
                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
              </svg>
            </div>
            <div className="flex-1 text-left">
              <div
                className="font-medium text-[#2D2F7E] dark:text-white"
                style={{ fontFamily: 'Sora, sans-serif' }}
              >
                {t('shareModal.whatsapp')}
              </div>
              <div
                className="text-sm text-gray-600 dark:text-white/70"
                style={{ fontFamily: 'Inter, sans-serif' }}
              >
                {t('shareModal.shareOnWhatsapp')}
              </div>
            </div>
          </button>

          {/* Email */}
          <button
            onClick={handleEmailShare}
            className="w-full flex items-center gap-4 p-4 border border-gray-200 dark:border-white/20 rounded-lg hover:bg-gray-50 dark:hover:bg-white/5 transition-colors group"
          >
            <div className="w-12 h-12 bg-blue-100 dark:bg-blue-900/20 rounded-full flex items-center justify-center group-hover:bg-blue-200 dark:group-hover:bg-blue-900/30 transition-colors">
              <Mail className="w-6 h-6 text-blue-600 dark:text-blue-400" />
            </div>
            <div className="flex-1 text-left">
              <div
                className="font-medium text-[#2D2F7E] dark:text-white"
                style={{ fontFamily: 'Sora, sans-serif' }}
              >
                {t('shareModal.email')}
              </div>
              <div
                className="text-sm text-gray-600 dark:text-white/70"
                style={{ fontFamily: 'Inter, sans-serif' }}
              >
                {t('shareModal.sendViaEmail')}
              </div>
            </div>
          </button>

          {/* Telegram */}
          <button
            onClick={handleTelegramShare}
            className="w-full flex items-center gap-4 p-4 border border-gray-200 dark:border-white/20 rounded-lg hover:bg-gray-50 dark:hover:bg-white/5 transition-colors group"
          >
            <div className="w-12 h-12 bg-sky-100 dark:bg-sky-900/20 rounded-full flex items-center justify-center group-hover:bg-sky-200 dark:group-hover:bg-sky-900/30 transition-colors">
              <svg
                className="w-6 h-6 text-sky-600 dark:text-sky-400"
                viewBox="0 0 24 24"
                fill="currentColor"
              >
                <path d="M11.944 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0a12 12 0 0 0-.056 0zm4.962 7.224c.1-.002.321.023.465.14a.506.506 0 0 1 .171.325c.016.093.036.306.02.472-.18 1.898-.962 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.14-5.061 3.345-.48.33-.913.49-1.302.48-.428-.008-1.252-.241-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635z" />
              </svg>
            </div>
            <div className="flex-1 text-left">
              <div
                className="font-medium text-[#2D2F7E] dark:text-white"
                style={{ fontFamily: 'Sora, sans-serif' }}
              >
                {t('shareModal.telegram')}
              </div>
              <div
                className="text-sm text-gray-600 dark:text-white/70"
                style={{ fontFamily: 'Inter, sans-serif' }}
              >
                {t('shareModal.shareOnTelegram')}
              </div>
            </div>
          </button>

          {/* Copy Link */}
          <button
            onClick={handleCopyLink}
            className="w-full flex items-center gap-4 p-4 border border-gray-200 dark:border-white/20 rounded-lg hover:bg-gray-50 dark:hover:bg-white/5 transition-colors group"
          >
            <div className="w-12 h-12 bg-purple-100 dark:bg-purple-900/20 rounded-full flex items-center justify-center group-hover:bg-purple-200 dark:group-hover:bg-purple-900/30 transition-colors">
              <MessageCircle className="w-6 h-6 text-purple-600 dark:text-purple-400" />
            </div>
            <div className="flex-1 text-left">
              <div
                className="font-medium text-[#2D2F7E] dark:text-white"
                style={{ fontFamily: 'Sora, sans-serif' }}
              >
                {t('shareModal.copyLink')}
              </div>
              <div
                className="text-sm text-gray-600 dark:text-white/70"
                style={{ fontFamily: 'Inter, sans-serif' }}
              >
                {t('shareModal.copyToClipboard')}
              </div>
            </div>
          </button>
        </div>
      </div>
    </div>
  );
}
